import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { eventsApi } from "../lib/api";
import { Camera, CheckCircle, Loader2, XCircle, Film, Sparkles, Music, Play } from "lucide-react";
import { Button } from "../components/ui/button";

export default function UnlockPage() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const [attempts, setAttempts] = useState(0);
  // Refs avoid stale closures inside the polling loop.
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    cancelledRef.current = false;
    attemptsRef.current = 0;

    // PIX/card webhooks usually land within ~10s, but PIX can take longer
    // for the bank to clear. We poll up to ~2 minutes before giving up.
    const MAX_ATTEMPTS = 48;       // ~2 minutes total polling
    const INTERVAL_MS = 2500;

    const poll = async () => {
      if (cancelledRef.current) return;
      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);

      try {
        const { data } = await eventsApi.paymentStatus(sessionId);
        if (cancelledRef.current) return;

        if (data?.payment_status === "paid") {
          setStatus("success");
          return;
        }
        // Only the "expired" status from Stripe should hard-fail. We
        // intentionally ignore `session_missing` because the Emergent
        // Stripe proxy occasionally returns 404 even for valid sessions.
        if (data?.status === "expired" && !data?.session_missing) {
          setStatus("failed");
          return;
        }
      } catch (err) {
        // 404 = the local record itself was not found → bail.
        if (err?.response?.status === 404) {
          setStatus("failed");
          return;
        }
        // Other network errors — keep polling until MAX_ATTEMPTS.
        console.warn("Polling payment status failed:", err?.message || err);
      }

      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("failed");
        return;
      }
      timerRef.current = setTimeout(poll, INTERVAL_MS);
    };

    poll();

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FFFBF7] flex flex-col" data-testid="unlock-page">
      <header className="w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
            <Camera className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-lg tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Festa.IA</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm text-center">
          {status === "checking" && (
            <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-8">
              <Loader2 className="w-12 h-12 animate-spin text-[#FF6B6B] mx-auto mb-4" />
              <h2 className="text-xl font-black text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Verificando pagamento...
              </h2>
              <p className="text-sm text-[#78716C]">Aguarde enquanto confirmamos seu pagamento</p>
              {attempts > 0 && (
                <p className="mt-2 text-xs text-[#A8A29E]" data-testid="unlock-attempts">
                  Tentativa {attempts} de 48
                </p>
              )}
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-8">
                <div className="w-16 h-16 bg-green-100 border-2 border-[#1C1917] rounded-full flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#1C1917]">
                  <CheckCircle className="w-8 h-8 text-green-600" strokeWidth={2.5} />
                </div>
                <h2 className="text-xl font-black text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Evento Desbloqueado!
                </h2>
                <p className="text-sm text-[#78716C] mb-6">
                  Todas as fotos e albums agora estao disponiveis!
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate(`/feed/${eventId}`)}
                    className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                    data-testid="go-to-feed-btn"
                  >
                    Ver Todas as Fotos
                  </Button>
                  <Button
                    onClick={() => navigate(`/albums/${eventId}`)}
                    variant="outline"
                    className="w-full border-2 border-[#1C1917] rounded-full font-bold shadow-[2px_2px_0px_#1C1917]"
                    data-testid="go-to-albums-btn"
                  >
                    Ver Albums
                  </Button>
                </div>
              </div>

              {/* Video Upsell */}
              <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#8B5CF6] rounded-xl overflow-hidden" data-testid="video-upsell-post-payment">
                <div className="bg-gradient-to-r from-[#8B5CF6] to-[#FF6B6B] p-4 text-white text-center">
                  <Sparkles className="w-6 h-6 mx-auto mb-1" strokeWidth={2.5} />
                  <p className="font-black text-sm uppercase tracking-wider">Oferta Especial</p>
                </div>
                <div className="p-5">
                  <h3 className="font-black text-[#1C1917] text-lg mb-2 text-center" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Video da Festa estilo Reels
                  </h3>
                  <p className="text-sm text-[#78716C] text-center mb-4">
                    30-60s com as melhores fotos, efeitos e musica a sua escolha
                  </p>
                  <div className="flex justify-center gap-4 mb-4">
                    {[
                      { icon: Play, text: "Estilo TikTok" },
                      { icon: Music, text: "Com musica" },
                      { icon: Film, text: "Download HD" },
                    ].map((item) => (
                      <span key={item.text} className="flex flex-col items-center gap-1 text-center">
                        <div className="w-10 h-10 bg-[#8B5CF6]/10 rounded-lg flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-[#8B5CF6]" strokeWidth={2.5} />
                        </div>
                        <span className="text-[10px] font-bold text-[#78716C]">{item.text}</span>
                      </span>
                    ))}
                  </div>
                  <div className="bg-[#FFFBF7] border-2 border-[#E7E5E4] rounded-xl p-3 text-center mb-4">
                    <p className="text-2xl font-black text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      R$ 49,90
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate(`/video/${eventId}`)}
                    className="w-full bg-[#8B5CF6] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                    data-testid="buy-video-upsell-btn"
                  >
                    <Film className="w-5 h-5 mr-2" strokeWidth={2.5} /> Criar Meu Video
                  </Button>
                </div>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-8">
              <div className="w-16 h-16 bg-red-100 border-2 border-[#1C1917] rounded-full flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#1C1917]">
                <XCircle className="w-8 h-8 text-red-600" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl font-black text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Pagamento nao confirmado
              </h2>
              <p className="text-sm text-[#78716C] mb-6">
                Nao conseguimos confirmar o pagamento. Se o valor foi cobrado, ele cai automaticamente em ate alguns minutos.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => window.location.reload()}
                  className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3"
                  data-testid="retry-unlock-btn"
                >
                  Tentar de novo
                </Button>
                <Button
                  onClick={() => navigate(`/feed/${eventId}`)}
                  variant="outline"
                  className="w-full border-2 border-[#1C1917] rounded-full font-bold shadow-[2px_2px_0px_#1C1917]"
                  data-testid="back-to-feed-btn"
                >
                  Voltar ao Feed
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
